import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './components/ui';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Jobs from './pages/Jobs';
import Blogs from './pages/Blogs';
import BlogDetail from './pages/BlogDetail';
import Services from './pages/Services';
import ForgotPassword from './pages/ForgotPassword';
import VerifyOTP from './pages/VerifyOTP';
import ResetPassword from './pages/ResetPassword';
import Opportunities from './pages/Opportunities';
import Articles from './pages/Articles';
import About from './pages/About';
import Contact from './pages/Contact';
import PostJob from './pages/PostJob';
import Pricing from './pages/Pricing';
import FAQ from './pages/FAQ';
import AdminDashboard from './pages/AdminDashboard';
import AdminRecruitmentMetrics from './pages/AdminRecruitmentMetrics';
import ManageBlogs from './pages/ManageBlogs';
import OpportunityDetail from './pages/OpportunityDetail';
import ServiceDetail from './pages/ServiceDetail';
import BlogForm from './pages/BlogForm';
import AdminSettings from './pages/AdminSettings';
import AdminUsers from './pages/AdminUsers';
import AdminJobs from './pages/AdminJobs';
import AdminJobForm from './pages/AdminJobForm';
import AdminServices from './pages/AdminServices';
import AdminServiceForm from './pages/AdminServiceForm';
import AdminPricing from './pages/AdminPricing';
import AdminFAQs from './pages/AdminFAQs';
import AdminContact from './pages/AdminContact';
import AdminAbout from './pages/AdminAbout';
import AdminOpportunities from './pages/AdminOpportunities';
import AdminOpportunityForm from './pages/AdminOpportunityForm';
import AdminArticles from './pages/AdminArticles';
import AdminArticleForm from './pages/AdminArticleForm';
import BusinessProfile from './pages/BusinessProfile';
import IndividualDashboard from './pages/IndividualDashboard';
import MyApplications from './pages/MyApplications';
import ApplyJob from './pages/ApplyJob';
import EmployerDashboard from './pages/employer/EmployerDashboard';
import EmployerApplications from './pages/employer/EmployerApplications';
import EmployerShortlisted from './pages/employer/EmployerShortlisted';
import EmployerHired from './pages/employer/EmployerHired';
import EmployerJobs from './pages/employer/EmployerJobs';
import EmployerProfile from './pages/employer/EmployerProfile';
import EmployerSettings from './pages/employer/EmployerSettings';
import PostJobWithLayout from './pages/employer/PostJobWithLayout';

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <ToastProvider />
        <div className="flex flex-col min-h-screen bg-gray-50">
          <Header />
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/blogs/:slug" element={<BlogDetail />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/opportunities" element={<Opportunities />} />
              <Route path="/opportunities/:slug" element={<OpportunityDetail />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/dashboard" element={
                <ProtectedRoute requiredRole="individual">
                  <IndividualDashboard />
                </ProtectedRoute>
              } />
              <Route path="/my-applications" element={
                <ProtectedRoute>
                  <MyApplications />
                </ProtectedRoute>
              } />
              <Route path="/jobs/:id/apply" element={
                <ProtectedRoute>
                  <ApplyJob />
                </ProtectedRoute>
              } />
              <Route path="/post-job" element={
                <ProtectedRoute requiredRole="business">
                  <PostJobWithLayout />
                </ProtectedRoute>
              } />
              <Route path="/business-profile" element={
                <ProtectedRoute requiredRole="business">
                  <BusinessProfile />
                </ProtectedRoute>
              } />

              {/* Employer Portal Routes - All under BusinessLayout */}
              <Route path="/employer" element={
                <ProtectedRoute requiredRole="business">
                  <EmployerDashboard />
                </ProtectedRoute>
              } />
              <Route path="/employer/dashboard" element={
                <ProtectedRoute requiredRole="business">
                  <EmployerDashboard />
                </ProtectedRoute>
              } />
              <Route path="/employer/post-job" element={
                <ProtectedRoute requiredRole="business">
                  <PostJobWithLayout />
                </ProtectedRoute>
              } />
              <Route path="/employer/jobs" element={
                <ProtectedRoute requiredRole="business">
                  <EmployerJobs />
                </ProtectedRoute>
              } />
              <Route path="/employer/applications" element={
                <ProtectedRoute requiredRole="business">
                  <EmployerApplications />
                </ProtectedRoute>
              } />
              <Route path="/employer/shortlisted" element={
                <ProtectedRoute requiredRole="business">
                  <EmployerShortlisted />
                </ProtectedRoute>
              } />
              <Route path="/employer/hired" element={
                <ProtectedRoute requiredRole="business">
                  <EmployerHired />
                </ProtectedRoute>
              } />
              <Route path="/employer/profile" element={
                <ProtectedRoute requiredRole="business">
                  <EmployerProfile />
                </ProtectedRoute>
              } />
              <Route path="/employer/settings" element={
                <ProtectedRoute requiredRole="business">
                  <EmployerSettings />
                </ProtectedRoute>
              } />

              <Route path="/admin" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/dashboard" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/recruitment-metrics" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminRecruitmentMetrics />
                </ProtectedRoute>
              } />
              <Route path="/admin/blogs" element={
                <ProtectedRoute requiredRole="admin">
                  <ManageBlogs />
                </ProtectedRoute>
              } />
              <Route path="/admin/blogs/create" element={
                <ProtectedRoute requiredRole="admin">
                  <BlogForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/blogs/edit/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <BlogForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSettings />
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminUsers />
                </ProtectedRoute>
              } />
              <Route path="/admin/jobs" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminJobs />
                </ProtectedRoute>
              } />
              <Route path="/admin/jobs/create" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminJobForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/jobs/edit/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminJobForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/services" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminServices />
                </ProtectedRoute>
              } />
              <Route path="/admin/services/create" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminServiceForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/services/edit/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminServiceForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/pricing" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminPricing />
                </ProtectedRoute>
              } />
              <Route path="/admin/faqs" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminFAQs />
                </ProtectedRoute>
              } />
              <Route path="/admin/contact" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminContact />
                </ProtectedRoute>
              } />
              <Route path="/admin/about" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAbout />
                </ProtectedRoute>
              } />
              <Route path="/admin/opportunities" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOpportunities />
                </ProtectedRoute>
              } />
              <Route path="/admin/opportunities/create" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOpportunityForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/opportunities/edit/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOpportunityForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/articles" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminArticles />
                </ProtectedRoute>
              } />
              <Route path="/admin/articles/create" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminArticleForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/articles/edit/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminArticleForm />
                </ProtectedRoute>
              } />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-otp" element={<VerifyOTP />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
